import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';

import { loadTinyMCE, tinyMCENeedsLoading } from '@/utils/tinymceLoader';

import { HtmlEditorFonts } from './HtmlEditorFonts';
import { normalizeEmptyBlockElements } from './normalizeEmptyBlockElements';
import { sizeFullySelectedBlocks } from './sizeFullySelectedBlocks';

import type { IStyle } from '../../../ThemeContext/ThemeObject';

const TOX_DIALOG_ZINDEX_FIX_ID = 'tox-dialog-zindex-fix';

interface IExecCommandEvent {
  command: string;
  value?: unknown;
}

// TinyMCE's own dialogs (link, source code, preview, image, table) render as
// body-level siblings with a hardcoded ~1300 z-index. This app's shared MUI
// modal z-index (ThemeContext.tsx: baseTheme.zIndex.modal + 10000 = 11300)
// then paints over them whenever this editor is used inside a modal, leaving
// the dialog present and focusable but invisible to the user.
const ensureToxDialogZIndexFix = () => {
  if (document.getElementById(TOX_DIALOG_ZINDEX_FIX_ID)) return;
  const style = document.createElement('style');
  style.id = TOX_DIALOG_ZINDEX_FIX_ID;
  style.textContent = '.tox-tinymce-aux { z-index: 12000 !important; }';
  document.head.appendChild(style);
};

// Legacy admin pages load TinyMCE on demand. Mount the React wrapper once the page's own build is
// in, since without a global TinyMCE the wrapper fetches one from Tiny's cloud.
const useTinyMCEReady = (): boolean => {
  const [ready, setReady] = useState(() => !tinyMCENeedsLoading());

  useEffect(() => {
    if (ready) return undefined;
    let mounted = true;
    const markReady = () => {
      if (mounted) setReady(true);
    };
    loadTinyMCE().then(markReady, markReady);
    return () => {
      mounted = false;
    };
  }, [ready]);

  return ready;
};

const HtmlEditor = ({
  body,
  onChange,
  onBlur,
  maxHeight,
  contentStyle,
  contentCss,
  bodyClass,
  readonlyEditor,
  minHeight,
  onEditorReady,
  resizable = false,
  normalizeEmptyBlocks = false,
  sizeSelectedBlocks = false
}: {
  body?: string;
  onChange?: (body: string) => void;
  onBlur?: (value: string) => void;
  maxHeight?: number;
  contentStyle?: string;
  contentCss?: string[];
  bodyClass?: string;
  readonlyEditor?: null | { body: string; styling: IStyle };
  minHeight?: number;
  onEditorReady?: (editor: unknown) => void;
  resizable?: boolean;
  normalizeEmptyBlocks?: boolean;
  sizeSelectedBlocks?: boolean;
}) => {
  const editorRef = useRef(null);
  const tinyMCEReady = useTinyMCEReady();
  const handleEditorAction = (callback?: (arg: string) => void) => {
    if (editorRef.current && callback) {
      callback(editorRef.current.getContent());
    }
  };
  const onEdit = () => handleEditorAction(onChange);
  const onBlurHandler = () => handleEditorAction(onBlur);

  if (!tinyMCEReady) return readonlyEditor ? null : <Box sx={{ minHeight: minHeight ?? 400 }} />;

  if (readonlyEditor)
    return (
      <Box sx={readonlyEditor.styling}>
        <Editor disabled initialValue={readonlyEditor.body} />
      </Box>
    );

  return (
    <Editor
      initialValue={body}
      onBlur={onBlurHandler}
      onChange={onEdit}
      init={{
        setup: (editor) => {
          if (normalizeEmptyBlocks) {
            editor.on('BeforeSetContent', (event) => {
              Object.assign(event, { content: normalizeEmptyBlockElements(event.content) });
            });
            editor.on('PastePostProcess', (event) => {
              Object.assign(event.node, {
                innerHTML: normalizeEmptyBlockElements(event.node.innerHTML)
              });
            });
          }
          // Registered before TinyMCE's undo manager, so the block sizes join the font size's undo level.
          if (sizeSelectedBlocks) {
            editor.on('ExecCommand', (event: IExecCommandEvent) => {
              if (event.command.toLowerCase() !== 'fontsize') return;
              sizeFullySelectedBlocks(
                editor.selection.getRng(),
                editor.selection.getSelectedBlocks(),
                String(event.value),
                (block, size) => editor.dom.setStyle(block, 'font-size', size)
              );
            });
          }
        },
        menubar: false,
        paste_data_images: false,
        // TinyMCE's drag handle lives in the status bar and is ignored while the
        // autoresize plugin is loaded, so a resizable editor swaps one for the other.
        ...(resizable
          ? {
              statusbar: true,
              resize: true,
              elementpath: false,
              branding: false,
              height: minHeight ?? 400,
              min_height: 200,
              max_height: maxHeight
            }
          : {
              statusbar: false,
              min_height: minHeight ?? 400,
              max_height: maxHeight ?? 1000
            }),
        autoresize_bottom_margin: 0,
        relative_urls: true,
        remove_script_host: false,
        browser_spellcheck: true,
        convert_urls: false,
        extended_valid_elements: 'div[*],span[*]',
        plugins: [
          'advlist',
          'autolink',
          ...(resizable ? [] : ['autoresize']),
          'lists',
          'link',
          'image',
          'charmap',
          'preview',
          'anchor',
          'media',
          'table',
          'code',
          'powerpaste'
        ],
        toolbar1:
          'bold italic underline forecolor backcolor | fontfamily fontsize blocks | undo redo',
        toolbar2:
          'alignleft aligncenter alignright alignjustify bullist numlist outdent indent | link image removeformat table | code preview',
        powerpaste_word_import: 'prompt',
        powerpaste_html_import: 'prompt',
        // contentCss replaces the Helvetica default but keeps TinyMCE's own content.css under it.
        ...(contentCss ? { content_css: ['default', ...contentCss] } : {}),
        body_class: bodyClass,
        content_style: `${
          contentStyle ??
          (contentCss ? '' : 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }')
        } ${HtmlEditorFonts.join(' ')}`,
        contextmenu: false,
        font_size_formats: '8px 10px 12px 14px 18px 24px 36px 8pt 10pt 12pt 14pt 18pt 24pt 36pt',
        font_family_formats:
          'Arial=arial,helvetica,sans-serif; Arial Black=arial black,avant garde; Book Antiqua=book antiqua,palatino; Comic Sans MS=comic sans ms,sans-serif; Courier New=courier new,courier; Georgia=georgia,palatino; Helvetica=helvetica; Impact=impact,chicago; Nexa=nexa; Symbol=symbol; Tahoma=tahoma,arial,helvetica,sans-serif; Terminal=terminal,monaco; Times New Roman=times new roman,times; Trebuchet MS=trebuchet ms,geneva; Verdana=verdana,geneva; Webdings=webdings; Wingdings=wingdings,zapf dingbats'
      }}
      onInit={(_evt, editor) => {
        editorRef.current = editor;
        ensureToxDialogZIndexFix();
        onEditorReady?.(editor);
      }}
    />
  );
};

export default HtmlEditor;
